
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import {
  wpMetaQueryCompare,
  wpMetaQueryType,
  WpMetaQueryCompare,
  WpMetaQueryType,
  WpMetaQueryData,
  WpMetaQuery
} from "@/types/wp-query";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import defensiveCopy from "@/util/defensive-copy";
import NiceButton from "@/components/UI/NiceButton.vue";

@Component({
  name: "WpMetaQueryComponent",
  components: { NiceButton, DropdownInput, Dropdown, TextInput }
})
export default class WpMetaQueryComponent extends Vue {
  @Prop()
  value?: WpMetaQuery;

  beforeCreate() {
    // @ts-ignore
    this.$options.components.Repeater =
      require("@/components/Reusable/CustomInputs/Repeater/Repeater.vue").default;
  }

  get defaultQuery(): WpMetaQueryData {
    return { key: "", value: "", compare: "=", type: "CHAR" };
  }

  get showRelationInput() {
    if (!this.value) return false;

    // better UX when doing a simple query
    if (this.value.metaQueries.length <= 1) return false;

    return this.value.relation;
  }

  updateRelation(newRelation: "OR" | "AND") {
    if (!this.value) return;
    const newValue = defensiveCopy(this.value);

    newValue.relation = newRelation;

    this.$emit("input", newValue);
  }

  updateMetaQueryData(metaQuery: (WpMetaQueryData | {})[]) {
    const metaQueryWithDefaultValues = metaQuery.map(
      (query): WpMetaQueryData =>
        Object.keys(query).length
          ? (query as WpMetaQueryData)
          : this.defaultQuery
    );

    let newValue = defensiveCopy(this.value);

    if (!metaQuery.length) {
      // remove empty queries, and also don't send garbage to php
      newValue = undefined;
    } else if (!newValue) {
      newValue = {
        relation: "AND",
        metaQueries: metaQueryWithDefaultValues
      };
    } else {
      newValue.metaQueries = metaQueryWithDefaultValues;
    }

    this.$emit("input", newValue);
  }

  updateNestedQuery(nestedQuery: WpMetaQuery | undefined, index: number) {
    if (!this.value) return;

    let newValue: WpMetaQuery | undefined = defensiveCopy(this.value);

    if (!nestedQuery) {
      newValue.metaQueries.splice(index, 1);

      // clear it
      if (!newValue.metaQueries.length) {
        newValue = undefined;
      }
    } else {
      newValue.metaQueries[index] = nestedQuery;
    }

    this.$emit("input", newValue);
  }

  get compareItems() {
    return wpMetaQueryCompare;
  }

  get typeItems() {
    return wpMetaQueryType;
  }

  showValue(value?: WpMetaQueryData) {
    if (!value) return true;

    if (value.compare === "EXISTS" || value.compare === "NOT EXISTS") {
      return false;
    }

    return true;
  }

  isValueAnArray(value?: WpMetaQueryData) {
    if (!value) return false;

    if (
      value.compare === "IN" ||
      value.compare === "NOT IN" ||
      value.compare === "BETWEEN" ||
      value.compare === "NOT BETWEEN"
    ) {
      return true;
    }

    return false;
  }

  updateKey(newKey: string, index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const value = newValue.metaQueries[index];

    if ("key" in value) {
      value.key = newKey;

      newValue.metaQueries[index] = value;
      this.$emit("input", newValue);
    }
  }

  updateValue(valueArg: string, index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const value = newValue.metaQueries[index];

    if ("value" in value) {
      value.value = valueArg;

      newValue.metaQueries[index] = value;

      this.$emit("input", newValue);
    }
  }

  updateValueArray(valueArg: (string | {})[], index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const value = newValue.metaQueries[index];

    if ("value" in value) {
      value.value = valueArg.map(value =>
        typeof value === "string" ? value : ""
      );

      newValue.metaQueries[index] = value;

      this.$emit("input", newValue);
    }
  }

  updateValueInArray(valueArg: string, parentIndex: number, index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const tempValue = newValue.metaQueries[parentIndex];

    if ("value" in tempValue && Array.isArray(tempValue.value)) {
      tempValue.value[index] = valueArg;

      newValue.metaQueries[parentIndex] = tempValue;

      this.$emit("input", newValue);
    }
  }

  updateCompare(newCompare: WpMetaQueryCompare, index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const tempValue = newValue.metaQueries[index];
    const currentValue = newValue.metaQueries[index];

    if ("compare" in tempValue && "compare" in currentValue) {
      tempValue.compare = newCompare;

      const valueTypeChanged =
        this.isValueAnArray(tempValue) !== this.isValueAnArray(currentValue);

      if (valueTypeChanged) {
        tempValue.value = this.isValueAnArray(tempValue) ? [] : "";
      }

      newValue.metaQueries[index] = tempValue;

      this.$emit("input", newValue);
    }
  }

  updateType(newType: WpMetaQueryType, index: number) {
    if (!this.value) return;

    const newValue = defensiveCopy(this.value);
    const tempValue = newValue.metaQueries[index];

    if ("type" in tempValue) {
      tempValue.type = newType;

      newValue.metaQueries[index] = tempValue;
      this.$emit("input", newValue);
    }
  }

  addNestedQuery() {
    let newValue = defensiveCopy(this.value);
    const newNestedQuery: WpMetaQuery = {
      relation: "AND",
      metaQueries: [this.defaultQuery]
    };

    if (newValue) {
      newValue.metaQueries.push(newNestedQuery);
    } else {
      newValue = {
        relation: "AND",
        metaQueries: [newNestedQuery]
      };
    }

    this.$emit("input", newValue);
  }
}
